import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: 0,
    marginBottom: 20,
  },
  svgContainer: {
    width: '100%',
    marginBottom: 20,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: 'rgba(17, 24, 39, 0.95)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.5,
    zIndex: 9999,
    maxWidth: 240,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
    marginTop: 8,
  },
  summaryCard: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: '12px 14px',
  },
  summaryLabel: {
    fontSize: 11,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  summaryDate: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
    marginBottom: 6,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: '#374151',
    marginBottom: 2,
  },
  summaryMagnitude: {
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
  },
  confidenceBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 4,
    marginTop: 4,
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: 14,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 8,
    marginTop: 4,
  },
};

const REGIME_COLORS = [
  'rgba(59, 130, 246, 0.06)',
  'rgba(16, 185, 129, 0.06)',
  'rgba(245, 158, 11, 0.06)',
  'rgba(139, 92, 246, 0.06)',
  'rgba(236, 72, 153, 0.06)',
];

function formatFieldName(field) {
  const map = {
    pest365: 'Pest 365',
    pest365Plus: 'Pest 365 Plus',
    initialServiceVisit: 'Initial Service Visit',
    oneTime: 'One-Time',
    mosquitoTickControl: 'Mosquito & Tick Control',
  };
  return map[field] || field;
}

export default function ChangepointDetectionChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const validData = useMemo(() => {
    if (!data || !data.dates || !data.values || data.dates.length === 0 || data.values.length === 0) {
      return null;
    }
    return data;
  }, [data]);

  const changepoints = useMemo(() => {
    if (!validData || !validData.changepoints) return [];
    return [...validData.changepoints].sort((a, b) => a.index - b.index);
  }, [validData]);

  const regimes = useMemo(() => {
    if (!validData) return [];
    const result = [];
    let start = 0;
    for (let i = 0; i < changepoints.length; i++) {
      result.push({
        startIndex: start,
        endIndex: changepoints[i].index - 1,
        mean: changepoints[i].beforeMean,
      });
      start = changepoints[i].index;
    }
    // Last regime after last changepoint
    const lastCp = changepoints[changepoints.length - 1];
    result.push({
      startIndex: start,
      endIndex: validData.dates.length - 1,
      mean: lastCp ? lastCp.afterMean : d3.mean(validData.values),
    });
    return result;
  }, [validData, changepoints]);

  useEffect(() => {
    if (!validData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 24, right: 30, bottom: 50, left: 55 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const parseDate = d3.timeParse('%Y-%m-%d');
    const dates = validData.dates.map((d) => parseDate(d));
    const values = validData.values;

    const xScale = d3.scaleTime()
      .domain(d3.extent(dates))
      .range([0, innerWidth]);

    const yMin = d3.min(values);
    const yMax = d3.max(values);
    const yPad = (yMax - yMin) * 0.15 || 1;
    const yScale = d3.scaleLinear()
      .domain([yMin - yPad, yMax + yPad])
      .range([innerHeight, 0]);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Regime shaded bands
    regimes.forEach((regime, i) => {
      const x0 = xScale(dates[regime.startIndex]);
      const x1 = xScale(dates[Math.min(regime.endIndex, dates.length - 1)]);
      g.append('rect')
        .attr('x', x0)
        .attr('y', 0)
        .attr('width', Math.max(x1 - x0, 2))
        .attr('height', innerHeight)
        .attr('fill', REGIME_COLORS[i % REGIME_COLORS.length])
        .attr('stroke', 'none');
    });

    // Gridlines
    const yAxis = d3.axisLeft(yScale).ticks(6);
    const yAxisG = g.append('g').call(yAxis);
    yAxisG.selectAll('line').attr('stroke', '#e5e7eb');
    yAxisG.selectAll('text').attr('fill', '#6b7280').attr('font-size', 11);
    yAxisG.select('.domain').attr('stroke', '#e5e7eb');

    // Horizontal gridlines
    g.append('g')
      .attr('class', 'grid')
      .call(
        d3.axisLeft(yScale)
          .ticks(6)
          .tickSize(-innerWidth)
          .tickFormat('')
      )
      .selectAll('line')
      .attr('stroke', '#f3f4f6')
      .attr('stroke-dasharray', '2,2');
    g.select('.grid .domain').remove();

    // X axis
    const xAxis = d3.axisBottom(xScale).ticks(6).tickFormat(d3.timeFormat('%b %d'));
    const xAxisG = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis);
    xAxisG.selectAll('line').attr('stroke', '#e5e7eb');
    xAxisG.selectAll('text').attr('fill', '#6b7280').attr('font-size', 11);
    xAxisG.select('.domain').attr('stroke', '#e5e7eb');

    // Regime mean segment lines
    regimes.forEach((regime, i) => {
      const x0 = xScale(dates[regime.startIndex]);
      const x1 = xScale(dates[Math.min(regime.endIndex, dates.length - 1)]);
      const y = yScale(regime.mean);
      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
      g.append('line')
        .attr('x1', x0)
        .attr('x2', x1)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', colors[i % colors.length])
        .attr('stroke-width', 3)
        .attr('stroke-opacity', 0.7)
        .attr('stroke-dasharray', '6,3');

      // Label the mean
      g.append('text')
        .attr('x', (x0 + x1) / 2)
        .attr('y', y - 8)
        .attr('text-anchor', 'middle')
        .attr('font-size', 10)
        .attr('fill', colors[i % colors.length])
        .attr('font-weight', 600)
        .text(`$${regime.mean.toFixed(2)}`);
    });

    // Vertical dashed lines at changepoints
    changepoints.forEach((cp) => {
      const cpDate = parseDate(cp.date);
      if (!cpDate) return;
      const x = xScale(cpDate);
      const color = cp.magnitude > 0 ? '#ef4444' : '#10b981';

      g.append('line')
        .attr('x1', x)
        .attr('x2', x)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,4')
        .attr('stroke-opacity', 0.8);

      // Confidence badge
      const badgeY = 14;
      const badgeW = 44;
      const badgeH = 18;
      g.append('rect')
        .attr('x', x - badgeW / 2)
        .attr('y', badgeY - badgeH / 2)
        .attr('width', badgeW)
        .attr('height', badgeH)
        .attr('rx', 4)
        .attr('fill', color)
        .attr('fill-opacity', 0.12)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.4);

      g.append('text')
        .attr('x', x)
        .attr('y', badgeY + 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', 10)
        .attr('font-weight', 700)
        .attr('fill', color)
        .text(`${(cp.confidence * 100).toFixed(0)}%`);

      // Arrow indicator at bottom
      const arrowY = innerHeight + 24;
      g.append('text')
        .attr('x', x)
        .attr('y', arrowY)
        .attr('text-anchor', 'middle')
        .attr('font-size', 9)
        .attr('fill', color)
        .attr('font-weight', 600)
        .text(cp.magnitude > 0 ? '\u25B2' : '\u25BC');
    });

    // Data line
    const line = d3.line()
      .x((_, i) => xScale(dates[i]))
      .y((d) => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(values)
      .attr('fill', 'none')
      .attr('stroke', '#374151')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Data dots
    g.selectAll('.data-dot')
      .data(values)
      .enter()
      .append('circle')
      .attr('cx', (_, i) => xScale(dates[i]))
      .attr('cy', (d) => yScale(d))
      .attr('r', 3)
      .attr('fill', '#fff')
      .attr('stroke', '#374151')
      .attr('stroke-width', 1.5);

    // Invisible hover targets
    g.selectAll('.hover-target')
      .data(values)
      .enter()
      .append('circle')
      .attr('cx', (_, i) => xScale(dates[i]))
      .attr('cy', (d) => yScale(d))
      .attr('r', 12)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        const i = values.indexOf(d);
        // Determine which regime this point belongs to
        let regimeIndex = 0;
        for (let r = 0; r < regimes.length; r++) {
          if (i >= regimes[r].startIndex && i <= regimes[r].endIndex) {
            regimeIndex = r;
            break;
          }
        }
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          date: validData.dates[i],
          value: d,
          regime: regimeIndex + 1,
          regimeMean: regimes[regimeIndex]?.mean,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -42)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('fill', '#6b7280')
      .text(formatFieldName(validData.field || 'pest365'));

  }, [validData, changepoints, regimes]);

  if (!validData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Structural Breakpoint Detection</h3>
        <p style={styles.subtitle}>Dates when the pricing model fundamentally shifted</p>
        <div style={styles.emptyState}>
          No changepoint data available. Provide pricing time series with detected breakpoints.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Structural Breakpoint Detection</h3>
      <p style={styles.subtitle}>Dates when the pricing model fundamentally shifted</p>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 350"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </div>

      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltip.date}</div>
          <div>Value: <strong>${tooltip.value.toFixed(2)}</strong></div>
          <div>Regime: <strong>#{tooltip.regime}</strong> (mean: ${tooltip.regimeMean?.toFixed(2)})</div>
        </div>
      )}

      {changepoints.length > 0 && (
        <>
          <div style={styles.summaryTitle}>
            Detected Breakpoints ({changepoints.length})
          </div>
          <div style={styles.summaryGrid}>
            {changepoints.map((cp, i) => {
              const isIncrease = cp.magnitude > 0;
              const color = isIncrease ? '#ef4444' : '#10b981';
              return (
                <div key={i} style={{ ...styles.summaryCard, borderLeft: `3px solid ${color}` }}>
                  <div style={styles.summaryLabel}>Breakpoint {i + 1}</div>
                  <div style={styles.summaryDate}>{cp.date}</div>
                  <div style={styles.summaryRow}>
                    <span>Before mean:</span>
                    <span style={{ fontWeight: 600 }}>${cp.beforeMean.toFixed(2)}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>After mean:</span>
                    <span style={{ fontWeight: 600 }}>${cp.afterMean.toFixed(2)}</span>
                  </div>
                  <div style={{ ...styles.summaryMagnitude, color }}>
                    {isIncrease ? '+' : ''}{cp.magnitude.toFixed(2)} ({isIncrease ? 'increase' : 'decrease'})
                  </div>
                  <div>
                    <span style={{
                      ...styles.confidenceBadge,
                      background: cp.confidence >= 0.9 ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                      color: cp.confidence >= 0.9 ? '#059669' : '#d97706',
                    }}>
                      {(cp.confidence * 100).toFixed(0)}% confidence
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
